import React, { useState } from 'react';
import { ArrowLeft, CheckCircle } from 'lucide-react';

interface VisaComplianceHubProps {
    onBack: () => void;
}

const VisaComplianceHub: React.FC<VisaComplianceHubProps> = ({ onBack }) => {
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        passportCountry: '',
        destinationCountry: 'UK',
        visaType: '',
        caseSummary: '',
        timeline: '12+ months',
        previousRefusal: false
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/student/submit-service', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    service: 'Visa Compliance Hub',
                    data: formData // The backend will convert this to string and store
                })
            });

            if (response.ok) {
                setSubmitted(true);
            } else {
                alert('Failed to submit query. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting query:', error);
            alert('An error occurred. Please check your connection.');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white p-8 rounded-2xl max-w-sm w-full text-center border border-gray-100 shadow-xl">
                    <div className="w-16 h-16 bg-[#009B8F] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#009B8F]/20">
                        <CheckCircle size={32} className="text-white" />
                    </div>
                    <h2 className="text-2xl font-serif font-bold text-slate-800 mb-2">Query Submitted</h2>
                    <p className="text-slate-500 text-sm mb-8 leading-relaxed">
                        Our team will review your case and get in touch with you shortly.
                    </p>
                    <button
                        onClick={onBack}
                        className="w-full bg-[#009B8F] hover:bg-[#007b72] text-white font-bold py-3 px-6 rounded-xl transition-all"
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            <button
                onClick={onBack}
                className="flex items-center text-slate-500 hover:text-[#009B8F] mb-6 transition-colors font-medium text-sm"
            >
                <ArrowLeft size={16} className="mr-2" /> Back to Dashboard
            </button>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                <div className="bg-[#011E26] p-8 flex items-center gap-4 relative overflow-hidden">
                    <div className="bg-[#009B8F]/20 p-3 rounded-lg text-[#009B8F]">
                        {/* Icon placeholder or reuse generic */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>
                    </div>
                    <div>
                        <h2 className="text-2xl font-serif font-bold text-white">Visa Compliance Hub</h2>
                        <p className="text-slate-400 text-sm">Expert guidance for your visa application process.</p>
                    </div>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#009B8F]/10 rounded-full -mr-8 -mt-8 pointer-events-none"></div>
                </div>

                <div className="p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                    Passport Issuing Country
                                </label>
                                <select
                                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-[#009B8F] transition-colors"
                                    required
                                    value={formData.passportCountry}
                                    onChange={(e) => setFormData({ ...formData, passportCountry: e.target.value })}
                                >
                                    <option value="">Select Country</option>
                                    <option value="India">India</option>
                                    <option value="Nigeria">Nigeria</option>
                                    <option value="China">China</option>
                                    <option value="Pakistan">Pakistan</option>
                                    <option value="Bangladesh">Bangladesh</option>
                                    <option value="Vietnam">Vietnam</option>
                                    <option value="Malaysia">Malaysia</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                    Destination Country
                                </label>
                                <select
                                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-[#009B8F] transition-colors"
                                    required
                                    value={formData.destinationCountry}
                                    onChange={(e) => setFormData({ ...formData, destinationCountry: e.target.value })}
                                >
                                    <option value="UK">UK</option>
                                    <option value="USA">USA</option>
                                    <option value="Canada">Canada</option>
                                    <option value="Australia">Australia</option>
                                    <option value="Europe">Europe</option>
                                    <option value="Dubai">Dubai (UAE)</option>
                                    <option value="Malaysia">Malaysia</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                    Current Visa Type
                                </label>
                                <select
                                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-[#009B8F] transition-colors"
                                    required
                                    value={formData.visaType}
                                    onChange={(e) => setFormData({ ...formData, visaType: e.target.value })}
                                >
                                    <option value="">Select Visa Type</option>
                                    <option value="Student Visa">Student Visa</option>
                                    <option value="Tourist Visa">Tourist Visa</option>
                                    <option value="Work Visa">Work Visa</option>
                                    <option value="Dependent Visa">Dependent Visa</option>
                                    <option value="No Visa">No Visa / Applying New</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                    Timeline
                                </label>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {['12+ months', '6-12 months', '3-6 months', '< 3 months'].map((time) => (
                                        <button
                                            key={time}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, timeline: time })}
                                            className={`px-3 py-2 rounded-lg text-xs font-bold border transition-all ${formData.timeline === time
                                                ? 'bg-[#009B8F] border-[#009B8F] text-white'
                                                : 'bg-white border-gray-200 text-slate-500 hover:border-[#009B8F]/50'
                                                }`}
                                        >
                                            {time}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                Brief Summary of Your Case
                            </label>
                            <textarea
                                className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:border-[#009B8F] transition-colors h-32"
                                placeholder="Please provide a brief summary of your visa query or situation..."
                                value={formData.caseSummary}
                                onChange={(e) => setFormData({ ...formData, caseSummary: e.target.value })}
                            />
                        </div>

                        <div className="flex items-center gap-4">
                            <label className="text-sm font-bold text-slate-700">Previous Refusal?</label>
                            <div className="flex bg-gray-100 rounded-lg p-1 border border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, previousRefusal: true })}
                                    className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${formData.previousRefusal ? 'bg-red-100 text-red-600 border border-red-200' : 'text-slate-400'
                                        }`}
                                >
                                    Yes
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, previousRefusal: false })}
                                    className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${!formData.previousRefusal ? 'bg-[#009B8F]/10 text-[#009B8F] border border-[#009B8F]/20' : 'text-slate-400'
                                        }`}
                                >
                                    No
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-[#011E26] hover:bg-[#009B8F] text-white font-bold py-4 rounded-xl shadow-sm transition-all"
                        >
                            {loading ? 'Submitting...' : 'Submit Query'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default VisaComplianceHub;
